import { useState } from "react";
import { useSearchParams } from "react-router";
import { Plus, Search } from "lucide-react";
import { ROTULO_DO_STATUS_NA_BASE, TIPOS_NA_BASE, type StatusNaBase, type TipoNaBase } from "@bookdental/schemas";
import { Card, CardContent, Input } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { ErroConsulta } from "@/components/ErroConsulta";
import { TabelaDaBase, type LinhaDaBase } from "@/components/base/TabelaDaBase";
import { EditorDaBase } from "@/components/base/EditorDaBase";
import { BarraDeSelecao } from "@/components/base/BarraDeSelecao";
import { DESCRICAO_DO_TIPO, ROTULO_DO_TIPO } from "@/components/base/rotulos";

const STATUS: StatusNaBase[] = ["PUBLICADO", "RASCUNHO", "ARQUIVADO"];
const semAcento = (t: string) => t.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/** Base de mercado — o catálogo de referência de onde os laboratórios copiam o que escolhem. */
export function AdminBaseDeMercado() {
  const [params, setParams] = useSearchParams();
  const tipo = (TIPOS_NA_BASE as readonly string[]).includes(params.get("tipo") ?? "")
    ? (params.get("tipo") as TipoNaBase)
    : "servicos";
  const [status, setStatus] = useState<StatusNaBase | undefined>();
  const [busca, setBusca] = useState("");
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [editando, setEditando] = useState<{ linha: LinhaDaBase | null } | null>(null);
  const selecionar = (ids: string[], marcar: boolean) =>
    setSelecionados((atual) => {
      const novo = new Set(atual);
      for (const i of ids) {
        if (marcar) novo.add(i);
        else novo.delete(i);
      }
      return novo;
    });

  const resumo = trpc.admin.base.resumo.useQuery();
  const lista = trpc.admin.base.listar.useQuery({ tipo, status });
  const termo = semAcento(busca.trim());
  const linhas = (lista.data ?? []).filter(
    (l) => !termo || semAcento(`${l.nome} ${l.codigo} ${l.grupo} ${l.detalhes.join(" ")}`).includes(termo),
  );
  const total = (t: TipoNaBase) =>
    Object.values(resumo.data?.find((r) => r.tipo === t)?.porStatus ?? {}).reduce((a, b) => a + (b ?? 0), 0);
  const doTipo = resumo.data?.find((r) => r.tipo === tipo)?.porStatus ?? {};

  return (
    <div className="mx-auto max-w-[1600px] space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Base de mercado</h1>
        <p className="mt-1 text-sm text-admin-700">
          Catálogo de referência curado pela plataforma. O laboratório escolhe daqui e recebe uma cópia no próprio
          catálogo, que guarda a origem.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-admin-200">
        {TIPOS_NA_BASE.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setParams({ tipo: t }, { replace: true });
              setBusca("");
              setSelecionados(new Set());
            }}
            aria-current={t === tipo ? "page" : undefined}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              t === tipo ? "border-admin-900 text-admin-900" : "border-transparent text-admin-700 hover:text-admin-900",
            )}
          >
            {ROTULO_DO_TIPO[t]}
            <span className="ml-1.5 text-xs text-admin-700/60 tabular-nums">{resumo.data ? total(t) : ""}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-admin-700">{DESCRICAO_DO_TIPO[tipo]}</p>
        <button
          type="button"
          onClick={() => setEditando({ linha: null })}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-admin-900 px-3 text-sm font-semibold text-white hover:bg-admin-700"
        >
          <Plus className="h-4 w-4" />
          Novo item
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-admin-700/50" />
          <Input
            placeholder={`Buscar em ${ROTULO_DO_TIPO[tipo].toLowerCase()}...`}
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="border-admin-200 bg-white pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[undefined, ...STATUS].map((s) => (
            <button
              key={s ?? "todos"}
              type="button"
              onClick={() => setStatus(s)}
              aria-pressed={status === s}
              className={cn(
                "rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors",
                status === s
                  ? "border-admin-900 bg-admin-900 text-white"
                  : "border-admin-200 bg-white text-admin-700 hover:bg-admin-50",
              )}
            >
              {s ? ROTULO_DO_STATUS_NA_BASE[s] : "Todos"}
              {s && doTipo[s] != null && <span className="ml-1 opacity-70 tabular-nums">{doTipo[s]}</span>}
            </button>
          ))}
        </div>
      </div>

      <Card className="border-admin-200">
        <CardContent className="p-0">
          {lista.error ? (
            <div className="p-5">
              <ErroConsulta error={lista.error} />
            </div>
          ) : lista.isLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-900 border-t-transparent" />
            </div>
          ) : linhas.length === 0 ? (
            <div className="py-16 text-center text-sm text-admin-700">Nenhum item encontrado.</div>
          ) : (
            <TabelaDaBase
              tipo={tipo}
              linhas={linhas}
              selecionados={selecionados}
              onSelecionar={selecionar}
              onAbrir={(linha) => setEditando({ linha })}
            />
          )}
        </CardContent>
      </Card>

      <BarraDeSelecao tipo={tipo} ids={[...selecionados]} onLimpar={() => setSelecionados(new Set())} />
      <EditorDaBase
        tipo={tipo}
        id={editando?.linha?.id ?? null}
        status={editando?.linha?.status}
        aberto={!!editando}
        onFechar={() => setEditando(null)}
      />
    </div>
  );
}
